import { useState } from "react";
import { FileUp, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { chamarMag } from "../funcoes";

/**
 * Documentos e pendências de uma proposta já registrada na MAG. Usado logo
 * depois do registro (cotação), na aba Propostas e na ficha do contato — a
 * assinatura pode chegar dias depois, e o vendedor volta por esses lugares.
 * Quem pode mexer é a RLS da cotação (dono/gerente/super admin), aplicada na
 * edge function `seguradoras-mag-proposta`, pela porta `seguradoras`.
 */

const SELECT = "h-9 rounded-md border border-input bg-transparent px-3 text-sm";

/** Os tipos da MAG que fazem sentido na venda (mesma lista de `mag/documento.ts`). */
const TIPOS_DOCUMENTO = [
  { id: "PropostaIndividual", nome: "Proposta assinada" },
  { id: "ComprovanteAssinaturaEletronica", nome: "Comprovante de assinatura eletrônica" },
  { id: "RG", nome: "RG" },
  { id: "CPF", nome: "CPF" },
  { id: "ComprovanteResidencia", nome: "Comprovante de residência" },
  { id: "ComprovanteRenda", nome: "Comprovante de renda" },
  { id: "DeclaracaoPessoalSaude", nome: "Declaração pessoal de saúde" },
  { id: "DocumentoAvulso", nome: "Outro documento" },
];
const LIMITE_PDF = 8 * 1024 * 1024;

/** Conteúdo do arquivo em base64, sem o prefixo "data:…;base64,". */
const lerBase64 = (arquivo: File) =>
  new Promise<string>((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(String(leitor.result).split(",")[1] ?? "");
    leitor.onerror = () => reject(leitor.error);
    leitor.readAsDataURL(arquivo);
  });

export interface DocumentoEnviado {
  tipo: string;
  nome: string;
  enviado_em: string;
}

/**
 * Depois do registro, a proposta fica "Pendente" na MAG até chegarem DOIS
 * PDFs: a imagem da proposta e o comprovante da assinatura (as pendências da
 * 113788317 no staging, 28/09/2026). Se a MAG abrir pendência, ela se
 * responde daqui com texto e PDF.
 */
// Nesta ordem: o comprovante faz a MAG reprocessar e reabrir a pendência da
// proposta; o PDF da proposta, enviado depois, responde essa pendência nova
// (o servidor responde a pendência aberta do mesmo grupo sozinho).
const EXIGIDOS = [
  { tipo: "ComprovanteAssinaturaEletronica", rotulo: "1. Comprovante da assinatura" },
  { tipo: "PropostaIndividual", rotulo: "2. Proposta assinada (PDF da proposta)" },
];

interface PendenciaMag {
  id: number;
  grupo: string;
  descricao: string | null;
  status: string | null;
  resposta: string | null;
}

const chamarProposta = <T,>(corpo: Record<string, unknown>, quando: string) => chamarMag<T>(corpo, quando);

/** PDF escolhido → base64, com as travas de tipo e tamanho. */
const prepararPdf = async (arquivo: File | undefined): Promise<{ base64: string; nome: string } | string> => {
  if (!arquivo) return "escolha um arquivo";
  if (arquivo.type !== "application/pdf" && !arquivo.name.toLowerCase().endsWith(".pdf")) return "o arquivo precisa ser PDF";
  if (arquivo.size > LIMITE_PDF) return "PDF de até 8 MB";
  return { base64: await lerBase64(arquivo), nome: arquivo.name };
};

const BotaoPdf = ({ rotulo, ocupado, aoEscolher }: { rotulo: string; ocupado: boolean; aoEscolher: (f?: File) => void }) => (
  <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-md border border-[#166534]/30 bg-white px-3 py-1.5 text-[12.5px] font-medium text-[#166534] hover:bg-[#dcfce7]">
    {ocupado ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
    {ocupado ? "Enviando…" : rotulo}
    <input
      type="file"
      accept="application/pdf,.pdf"
      className="sr-only"
      disabled={ocupado}
      onChange={(e) => {
        aoEscolher(e.target.files?.[0]);
        e.target.value = "";
      }}
    />
  </label>
);

export const DocumentosDaProposta = ({
  cotacaoId,
  documentosIniciais = [],
}: {
  cotacaoId: number;
  documentosIniciais?: DocumentoEnviado[];
}) => {
  const [enviando, setEnviando] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviados, setEnviados] = useState<DocumentoEnviado[]>(documentosIniciais);
  const [outroTipo, setOutroTipo] = useState("RG");
  const [pendencias, setPendencias] = useState<PendenciaMag[] | null>(null);
  const [respostas, setRespostas] = useState<Record<number, string>>({});
  const [aviso, setAviso] = useState<string | null>(null);

  const enviarDocumento = async (tipo: string, arquivo?: File) => {
    setErro(null);
    const pdf = await prepararPdf(arquivo);
    if (typeof pdf === "string") return setErro(pdf);
    setEnviando(tipo);
    setAviso(null);
    const { dados, erro: falha } = await chamarProposta<{ documentos: DocumentoEnviado[]; pendenciasRespondidas?: number }>(
      { cotacaoId, acao: "documento", documento: { tipo, nomeArquivo: pdf.nome, base64: pdf.base64 } },
      "não foi possível enviar o documento à MAG",
    );
    setEnviando(null);
    if (falha || !dados) return setErro(falha ?? "a MAG não confirmou o documento");
    setEnviados(dados.documentos);
    if (dados.pendenciasRespondidas) {
      setAviso(dados.pendenciasRespondidas === 1 ? "Pendência da MAG também respondida com este PDF." : `${dados.pendenciasRespondidas} pendências da MAG também respondidas com este PDF.`);
    }
  };

  const verPendencias = async () => {
    setErro(null);
    setEnviando("pendencias");
    const { dados, erro: falha } = await chamarProposta<{ pendencias: PendenciaMag[]; documentos: DocumentoEnviado[] }>(
      { cotacaoId, acao: "pendencias" },
      "não foi possível consultar as pendências na MAG",
    );
    setEnviando(null);
    if (falha || !dados) return setErro(falha ?? "a MAG não respondeu");
    setPendencias(dados.pendencias);
    setEnviados(dados.documentos);
  };

  const responder = async (p: PendenciaMag, arquivo?: File) => {
    setErro(null);
    const resposta = (respostas[p.id] ?? "").trim();
    if (!resposta) return setErro("escreva a resposta da pendência");
    let documento: Record<string, string> | undefined;
    if (arquivo) {
      const pdf = await prepararPdf(arquivo);
      if (typeof pdf === "string") return setErro(pdf);
      // O grupo diz o que a MAG pediu: pendência de assinatura leva o comprovante.
      const tipo = /ASSINATURA/i.test(p.grupo) ? "ComprovanteAssinaturaEletronica" : /PROPOSTA/i.test(p.grupo) ? "PropostaIndividual" : "DocumentoAvulso";
      documento = { tipo, nomeArquivo: pdf.nome, base64: pdf.base64 };
    }
    setEnviando(`p${p.id}`);
    const { erro: falha } = await chamarProposta<{ respondida: boolean }>(
      { cotacaoId, acao: "responder", pendenciaId: p.id, resposta, ...(documento ? { documento } : {}) },
      "não foi possível responder a pendência",
    );
    setEnviando(null);
    if (falha) return setErro(falha);
    await verPendencias();
  };

  const enviado = (tipo: string) => enviados.some((d) => d.tipo === tipo);
  const abertas = (pendencias ?? []).filter((p) => (p.status ?? "").toUpperCase() !== "RESOLVIDO");

  return (
    <div className="mt-3 flex flex-col gap-3 border-t border-[#bbf7d0] pt-3">
      <div>
        <div className="text-[13px] font-semibold text-[#166534]">Assinatura e documentos</div>
        <p className="text-[12.5px] text-[#166534]/80">
          A MAG analisa a proposta após receber os dois PDFs abaixo, nesta ordem. Documento assinado em papel e
          digitalizado vale para ambos. Pendências abertas pela MAG são respondidas nesta seção.
        </p>
      </div>

      <ul className="flex flex-col gap-2">
        {EXIGIDOS.map((d) => (
          <li key={d.tipo} className="flex flex-wrap items-center justify-between gap-2 text-[12.5px] text-[#166534]">
            <span className={enviado(d.tipo) ? "font-semibold" : ""}>
              {enviado(d.tipo) ? "✓" : "○"} {d.rotulo}
            </span>
            <BotaoPdf
              rotulo={enviado(d.tipo) ? "Enviar outro" : "Enviar PDF"}
              ocupado={enviando === d.tipo}
              aoEscolher={(f) => enviarDocumento(d.tipo, f)}
            />
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12.5px] text-[#166534]">Outro documento:</span>
        <select
          aria-label="Tipo de documento"
          className={`${SELECT} h-8 bg-white`}
          value={outroTipo}
          onChange={(e) => setOutroTipo(e.target.value)}
        >
          {TIPOS_DOCUMENTO.filter((t) => !EXIGIDOS.some((x) => x.tipo === t.id)).map((t) => (
            <option key={t.id} value={t.id}>{t.nome}</option>
          ))}
        </select>
        <BotaoPdf rotulo="Enviar PDF" ocupado={enviando === outroTipo} aoEscolher={(f) => enviarDocumento(outroTipo, f)} />
      </div>

      <div className="flex flex-col gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-fit bg-white"
          onClick={verPendencias}
          disabled={enviando === "pendencias"}
        >
          {enviando === "pendencias" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Ver pendências na MAG
        </Button>
        {pendencias && abertas.length === 0 && (
          <p className="text-[12.5px] text-[#166534]">Nenhuma pendência aberta na MAG.</p>
        )}
        {abertas.map((p) => (
          <div key={p.id} className="flex flex-col gap-2 rounded-md border border-[#fcd34d] bg-[#fffbeb] px-3 py-2">
            <div className="text-[12.5px] font-semibold text-[#92400e]">{p.grupo || "Pendência"}</div>
            <p className="text-[12.5px] text-[#92400e]">{p.descricao}</p>
            <Input
              aria-label={`Resposta da pendência ${p.id}`}
              placeholder="Resposta para a MAG"
              className="bg-white"
              value={respostas[p.id] ?? ""}
              onChange={(e) => setRespostas((r) => ({ ...r, [p.id]: e.target.value }))}
            />
            <div className="flex flex-wrap gap-2">
              <BotaoPdf rotulo="Responder com PDF" ocupado={enviando === `p${p.id}`} aoEscolher={(f) => responder(p, f)} />
              <Button type="button" size="sm" variant="ghost" onClick={() => responder(p)} disabled={enviando === `p${p.id}`}>
                Responder sem anexo
              </Button>
            </div>
          </div>
        ))}
      </div>

      {aviso && <p className="text-[12.5px] font-medium text-[#166534]">{aviso}</p>}
      {erro && (
        <p role="alert" className="text-[12.5px] text-destructive">
          {erro}
        </p>
      )}
    </div>
  );
};
